/**
 * Admin – Events & Classes
 *
 * Tammy uses this page to create and manage individual events such as
 * "Cake Decorating Class – May 8".  She can set the date, time, price,
 * available spots, image, and a description.  Each event can be linked to
 * an existing category (Decorating Classes, Birthday Parties, etc.).
 *
 * Workflow:
 *   1. Click "Add Event"
 *   2. Fill in title, date, time, price, spots, description
 *   3. Toggle visible ON to make it show on the site
 *   4. Edit or delete any time
 */

import { useState, useEffect } from 'react';
import {
  HiPlus, HiPencil, HiTrash, HiEye, HiEyeOff, HiCalendar,
  HiClock, HiLocationMarker, HiTicket, HiCurrencyDollar,
} from 'react-icons/hi';
import { adminFetch, adminHeaders } from '../lib/adminApi';

// ─── Types ────────────────────────────────────────────────────────────────────

interface EventCategory {
  id: string;
  name: string;
  slug: string;
  color: string;
}

interface BakeryEvent {
  id: string;
  category_id: string | null;
  category?: EventCategory | null;
  title: string;
  description: string | null;
  event_date: string | null;   // ISO date string "YYYY-MM-DD"
  event_time: string | null;   // e.g. "10:00 AM – 2:00 PM"
  location: string | null;
  price: number | null;
  price_text: string | null;
  spots_total: number | null;
  spots_remaining: number | null;
  image_url: string | null;
  registration_url: string | null;
  is_visible: boolean;
  is_featured: boolean;
}

// Blank form state used for both "create" and "edit"
const BLANK: Omit<BakeryEvent, 'id' | 'category'> = {
  category_id: null,
  title: '',
  description: null,
  event_date: null,
  event_time: null,
  location: null,
  price: null,
  price_text: null,
  spots_total: null,
  spots_remaining: null,
  image_url: null,
  registration_url: null,
  is_visible: true,
  is_featured: false,
};

const API_URL = import.meta.env.VITE_API_URL || '';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Format "2026-05-08" → "Thu May 8, 2026" */
function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso + 'T12:00:00'); // noon avoids timezone shift
  return d.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function Events() {
  const [events, setEvents]         = useState<BakeryEvent[]>([]);
  const [categories, setCategories] = useState<EventCategory[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);

  // Modal state
  const [showModal, setShowModal]       = useState(false);
  const [editing, setEditing]           = useState<BakeryEvent | null>(null); // null = create mode
  const [form, setForm]                 = useState<Omit<BakeryEvent, 'id' | 'category'>>(BLANK);
  const [saving, setSaving]             = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null); // event id to confirm

  // ─── Fetch ──────────────────────────────────────────────────────────────────

  async function fetchEvents() {
    try {
      const res = await adminFetch(`${API_URL}/api/events`);
      if (!res.ok) throw new Error('Failed to load events');
      const data = await res.json();
      setEvents(data.events || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }

  async function fetchCategories() {
    try {
      const res = await adminFetch(`${API_URL}/api/event-categories`);
      if (!res.ok) return;
      const data = await res.json();
      setCategories(data.categories || []);
    } catch {
      // non-fatal — categories are optional
    }
  }

  useEffect(() => {
    fetchEvents();
    fetchCategories();
  }, []);

  // ─── Modal helpers ──────────────────────────────────────────────────────────

  function openCreate() {
    setEditing(null);
    setForm(BLANK);
    setShowModal(true);
  }

  function openEdit(evt: BakeryEvent) {
    setEditing(evt);
    setForm({
      category_id:      evt.category_id,
      title:            evt.title,
      description:      evt.description,
      event_date:       evt.event_date,
      event_time:       evt.event_time,
      location:         evt.location,
      price:            evt.price,
      price_text:       evt.price_text,
      spots_total:      evt.spots_total,
      spots_remaining:  evt.spots_remaining,
      image_url:        evt.image_url,
      registration_url: evt.registration_url,
      is_visible:       evt.is_visible,
      is_featured:      evt.is_featured,
    });
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditing(null);
  }

  // ─── Save (create or update) ─────────────────────────────────────────────────

  async function handleSave() {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const url    = editing ? `${API_URL}/api/events/${editing.id}` : `${API_URL}/api/events`;
      const method = editing ? 'PUT' : 'POST';

      const res = await adminFetch(url, {
        method,
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Save failed');
      }

      await fetchEvents();
      closeModal();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  // ─── Delete ──────────────────────────────────────────────────────────────────

  async function handleDelete(id: string) {
    try {
      const res = await adminFetch(`${API_URL}/api/events/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      setEvents(prev => prev.filter(e => e.id !== id));
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setConfirmDelete(null);
    }
  }

  // ─── Quick toggle visible ────────────────────────────────────────────────────

  async function toggleVisible(evt: BakeryEvent) {
    try {
      await adminFetch(`${API_URL}/api/events/${evt.id}`, {
        method: 'PUT',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ is_visible: !evt.is_visible }),
      });
      setEvents(prev => prev.map(e => e.id === evt.id ? { ...e, is_visible: !e.is_visible } : e));
    } catch {
      alert('Could not update visibility');
    }
  }

  // ─── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        Loading events…
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 p-6">{error}</div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Events &amp; Classes</h1>
          <p className="text-sm text-gray-500 mt-1">
            Add decorating classes, parties, and in-store events. Toggle them on/off any time.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium text-sm"
        >
          <HiPlus className="w-5 h-5" />
          Add Event
        </button>
      </div>

      {/* Empty state */}
      {events.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <HiCalendar className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="text-lg font-medium">No events yet</p>
          <p className="text-sm">Click "Add Event" to create your first one.</p>
        </div>
      )}

      {/* Event cards */}
      <div className="grid gap-4">
        {events.map(evt => (
          <div
            key={evt.id}
            className={`bg-white border rounded-xl p-5 flex gap-4 items-start shadow-sm transition ${
              evt.is_visible ? '' : 'opacity-60'
            }`}
          >
            {/* Thumbnail */}
            {evt.image_url ? (
              <img
                src={evt.image_url}
                alt={evt.title}
                className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-20 h-20 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                <HiCalendar className="w-8 h-8 text-purple-300" />
              </div>
            )}

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  {/* Category badge */}
                  {evt.category && (
                    <span
                      className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full mb-1"
                      style={{ backgroundColor: evt.category.color + '22', color: evt.category.color }}
                    >
                      {evt.category.name}
                    </span>
                  )}
                  <h3 className="font-semibold text-gray-900 text-base leading-tight">{evt.title}</h3>
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* Visibility toggle */}
                  <button
                    onClick={() => toggleVisible(evt)}
                    title={evt.is_visible ? 'Hide from site' : 'Show on site'}
                    className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
                  >
                    {evt.is_visible ? <HiEye className="w-4 h-4 text-green-600" /> : <HiEyeOff className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => openEdit(evt)}
                    title="Edit"
                    className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
                  >
                    <HiPencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setConfirmDelete(evt.id)}
                    title="Delete"
                    className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600"
                  >
                    <HiTrash className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Meta row */}
              <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-500">
                {evt.event_date && (
                  <span className="flex items-center gap-1">
                    <HiCalendar className="w-3.5 h-3.5" />
                    {formatDate(evt.event_date)}
                  </span>
                )}
                {evt.event_time && (
                  <span className="flex items-center gap-1">
                    <HiClock className="w-3.5 h-3.5" />
                    {evt.event_time}
                  </span>
                )}
                {evt.location && (
                  <span className="flex items-center gap-1">
                    <HiLocationMarker className="w-3.5 h-3.5" />
                    {evt.location}
                  </span>
                )}
                {(evt.price_text || evt.price != null) && (
                  <span className="flex items-center gap-1">
                    <HiCurrencyDollar className="w-3.5 h-3.5" />
                    {evt.price_text || `$${evt.price}`}
                  </span>
                )}
                {evt.spots_total != null && (
                  <span className="flex items-center gap-1">
                    <HiTicket className="w-3.5 h-3.5" />
                    {evt.spots_remaining ?? evt.spots_total} / {evt.spots_total} spots
                  </span>
                )}
              </div>

              {evt.description && (
                <p className="mt-2 text-sm text-gray-600 line-clamp-2">{evt.description}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* ── Add / Edit Modal ──────────────────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">
                {editing ? 'Edit Event' : 'Add New Event'}
              </h2>
            </div>

            <div className="p-6 space-y-5">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Cake Decorating Class"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={form.category_id || ''}
                  onChange={e => setForm(f => ({ ...f, category_id: e.target.value || null }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  aria-label="Event category"
                  title="Event category"
                >
                  <option value="">— No category —</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              {/* Date + Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={form.event_date || ''}
                    onChange={e => setForm(f => ({ ...f, event_date: e.target.value || null }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    aria-label="Event date"
                    title="Event date"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                  <input
                    type="text"
                    value={form.event_time || ''}
                    onChange={e => setForm(f => ({ ...f, event_time: e.target.value || null }))}
                    placeholder="e.g. 10:00 AM – 2:00 PM"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input
                  type="text"
                  value={form.location || ''}
                  onChange={e => setForm(f => ({ ...f, location: e.target.value || null }))}
                  placeholder="Leave blank to use the bakery address"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Price */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price (number)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.price ?? ''}
                    onChange={e => setForm(f => ({ ...f, price: e.target.value ? parseFloat(e.target.value) : null }))}
                    placeholder="e.g. 45"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    aria-label="Price amount"
                    title="Price amount"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price Display Text</label>
                  <input
                    type="text"
                    value={form.price_text || ''}
                    onChange={e => setForm(f => ({ ...f, price_text: e.target.value || null }))}
                    placeholder='e.g. "$45 per person"'
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              {/* Spots */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Spots</label>
                  <input
                    type="number"
                    min="1"
                    value={form.spots_total ?? ''}
                    onChange={e => setForm(f => ({ ...f, spots_total: e.target.value ? parseInt(e.target.value) : null }))}
                    placeholder="Leave blank = unlimited"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    aria-label="Total spots available"
                    title="Total spots available"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Spots Remaining</label>
                  <input
                    type="number"
                    min="0"
                    value={form.spots_remaining ?? ''}
                    onChange={e => setForm(f => ({ ...f, spots_remaining: e.target.value ? parseInt(e.target.value) : null }))}
                    placeholder="Update as people sign up"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    aria-label="Spots remaining"
                    title="Spots remaining"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  rows={4}
                  value={form.description || ''}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value || null }))}
                  placeholder="Tell people what to expect, what's included, what to bring, etc."
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  aria-label="Event description"
                  title="Event description"
                />
              </div>

              {/* Image URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
                <input
                  type="url"
                  value={form.image_url || ''}
                  onChange={e => setForm(f => ({ ...f, image_url: e.target.value || null }))}
                  placeholder="Paste an image URL or leave blank"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Registration link */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Registration Link (optional)</label>
                <input
                  type="url"
                  value={form.registration_url || ''}
                  onChange={e => setForm(f => ({ ...f, registration_url: e.target.value || null }))}
                  placeholder="Link to sign-up page, Eventbrite, etc."
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Toggles */}
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_visible}
                    onChange={e => setForm(f => ({ ...f, is_visible: e.target.checked }))}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">Visible on site</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_featured}
                    onChange={e => setForm(f => ({ ...f, is_featured: e.target.checked }))}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">Featured (highlight on homepage)</span>
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.title.trim()}
                className="px-5 py-2 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : (editing ? 'Save Changes' : 'Create Event')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete confirmation ───────────────────────────────────────────── */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Event?</h3>
            <p className="text-gray-600 text-sm mb-6">
              This will permanently remove the event from the site. This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
